<template>
  <div class="relative bg-white pt-16 pb-16">
    <div class="px-4 md:px-10 mx-auto w-full">
      <div class="flex flex-wrap mt-6">
        <div class="w-full px-4">
          <div class="verification-detail-card relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
            <div class="rounded-t mb-0 px-6 py-3 border-0">
              <div class="flex flex-wrap items-center justify-between">
                <div class="relative w-full px-4 max-w-full flex-grow flex-1">
                  <div class="verification-detail-title flex items-center">
                    <router-link
                      to="/admin/customers/verification"
                      class="flex items-center text-blue-500 hover:text-blue-700 mr-4 text-sm"
                    >
                      <i class="fas fa-arrow-left mr-2"></i>
                      返回列表
                    </router-link>
                    <h3 class="font-semibold text-base text-blueGray-700">
                      认证申请详情
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            <!-- 加载状态 -->
            <div v-if="loading" class="flex justify-center items-center py-20">
              <el-icon class="is-loading" :size="24">
                <i class="fas fa-spinner fa-spin"></i>
              </el-icon>
              <span class="ml-2 text-gray-500">加载中...</span>
            </div>

            <!-- 详情内容 -->
            <div v-else-if="application" class="block w-full px-6 pb-6">
              <div class="verification-detail-meta flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
                <el-tag :type="getStatusTagType(application.status)" size="large">
                  {{ getStatusLabel(application.status) }}
                </el-tag>
                <div class="text-sm text-gray-500">
                  申请时间：{{ formatDate(application.createdAt) }}
                </div>
              </div>

              <!-- 基本信息 -->
              <h4 class="text-lg font-semibold text-gray-800 mb-4">基本信息</h4>
              <el-descriptions
                class="verification-descriptions"
                :column="descriptionColumn"
                border
              >
                <el-descriptions-item label="运营人员姓名">
                  {{ application.username || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="主体名称">
                  {{ application.full_name || '-' }}
                </el-descriptions-item>
                <el-descriptions-item label="UnionID">
                  <span class="font-mono text-sm">{{ application.id || '-' }}</span>
                </el-descriptions-item>
                <el-descriptions-item label="联系电话">
                  {{ application.op_phone || '未提供' }}
                </el-descriptions-item>
                <el-descriptions-item label="申请类型">
                  <el-tag :type="getTypeTagType(application.type)" size="small">
                    {{ getTypeLabel(application.type) }}
                  </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="审核时间">
                  {{ application.auth_time ? formatDate(application.auth_time) : '未审核' }}
                </el-descriptions-item>
              </el-descriptions>

              <!-- 拒绝理由 -->
              <div v-if="application.status == 3" class="mt-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4">拒绝理由</h4>
                <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p class="text-red-700">{{ application.reason || '暂无备注信息' }}</p>
                </div>
              </div>

              <!-- 相关凭证（非个人认证时显示） -->
              <div v-if="application.type !== 1 && application.documents && application.documents.length > 0" class="mt-6">
                <h4 class="text-lg font-semibold text-gray-800 mb-4">相关凭证</h4>
                <div class="flex flex-wrap gap-4">
                  <div
                    v-for="doc in application.documents"
                    :key="doc.id"
                    class="flex items-center gap-3 bg-gray-50 px-4 py-3 rounded-lg"
                  >
                    <i class="fas fa-file-image text-blue-500 text-xl"></i>
                    <span class="text-sm text-gray-700">{{ doc.name }}</span>
                    <el-link
                      type="primary"
                      size="small"
                      @click="openDocumentModal(doc)"
                    >
                      <i class="fas fa-external-link-alt ml-2"></i>
                      查看
                    </el-link>
                  </div>
                </div>
              </div>

              <!-- 审核操作 -->
              <div v-if="application.status === 1" class="mt-8 pt-6 border-t border-gray-100">
                <h4 class="text-lg font-semibold text-gray-800 mb-4">审核操作</h4>
                <div class="flex flex-wrap gap-4">
                  <el-button type="success" size="large" @click="approveApplication">
                    <i class="fas fa-check mr-2"></i>
                    通过
                  </el-button>
                  <el-button type="danger" size="large" @click="openRejectModal">
                    <i class="fas fa-times mr-2"></i>
                    拒绝
                  </el-button>
                  <el-button
                    v-if="application.type === 4 && !isApplicationVerified(application)"
                    type="warning"
                    size="large"
                    @click="openSmsModal"
                  >
                    <i class="fas fa-sms mr-2"></i>
                    发送短信验证
                  </el-button>
                </div>
              </div>
            </div>

            <!-- 无数据 -->
            <div v-else class="text-center py-20">
              <i class="fas fa-exclamation-circle text-gray-300 text-5xl mb-4"></i>
              <p class="text-gray-500 text-lg">未找到该认证申请</p>
              <router-link
                to="/admin/customers/verification"
                class="inline-block mt-4 text-blue-500 hover:text-blue-700"
              >
                返回列表
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 拒绝理由弹窗 -->
    <el-dialog
      v-model="showRejectModal"
      title="拒绝理由"
      width="500px"
      :before-close="closeRejectModal"
    >
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">预设理由：</label>
        <el-select
          v-model="selectedPresetReason"
          placeholder="请选择预设理由（可选）"
          clearable
          class="w-full mb-4"
          @change="handlePresetReasonChange"
        >
          <el-option
            v-for="reason in presetReasons"
            :key="reason"
            :label="reason"
            :value="reason"
          />
        </el-select>

        <label class="block text-sm font-medium text-gray-700 mb-2">拒绝理由：</label>
        <el-input
          v-model="rejectReason"
          type="textarea"
          :rows="5"
          placeholder="请输入拒绝理由"
          :maxlength="500"
          show-word-limit
        />
        <p v-if="rejectError" class="text-red-500 text-sm mt-2">{{ rejectError }}</p>
      </div>
      <template #footer>
        <el-button @click="closeRejectModal">取消</el-button>
        <el-button type="danger" :loading="loading" @click="confirmReject">确认拒绝</el-button>
      </template>
    </el-dialog>

    <!-- 凭证预览弹窗 -->
    <el-dialog
      v-model="showDocumentModal"
      :title="previewDocument ? previewDocument.name : '查看凭证'"
      width="90%"
      top="4vh"
      :before-close="closeDocumentModal"
    >
      <div
        v-if="previewDocument"
        class="flex justify-center bg-gray-50 rounded-lg p-4"
        style="min-height: 60vh;"
      >
        <img
          v-if="isImageDocument(previewDocument)"
          :src="previewDocument.url"
          :alt="previewDocument.name"
          class="max-w-full object-contain rounded"
          style="max-height: 76vh;"
        />
        <iframe
          v-else
          :src="previewDocument.url"
          :title="previewDocument.name"
          class="w-full bg-white rounded"
          style="height: 76vh;"
        ></iframe>
      </div>
      <template #footer>
        <el-button @click="closeDocumentModal">关闭</el-button>
        <el-button
          v-if="previewDocument && previewDocument.url"
          type="primary"
          tag="a"
          :href="previewDocument.url"
          target="_blank"
        >
          新窗口打开
        </el-button>
      </template>
    </el-dialog>

    <!-- 短信验证弹窗 -->
    <el-dialog
      v-model="showSmsModal"
      title="短信验证"
      width="500px"
      :before-close="closeSmsModal"
    >
      <div v-if="application" class="space-y-4">
        <div class="mb-4">
          <p class="text-sm text-gray-600 mb-2">
            正在为 <strong>{{ application.username }}</strong> 的公众号认证申请进行短信验证
          </p>
          <p class="text-sm text-gray-500">
            手机号：{{ application.phone || '未提供手机号' }}
          </p>
        </div>

        <div v-if="!isSmsSent">
          <el-button
            type="primary"
            @click="sendSmsVerification"
            :loading="loading"
            class="w-full"
          >
            发送验证码
          </el-button>
        </div>

        <div v-else>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              请输入收到的验证码：
            </label>
            <el-input
              v-model="verificationCode"
              placeholder="请输入6位验证码"
              :maxlength="6"
              @keyup.enter="verifyCode"
            />
            <p v-if="smsCountdown > 0" class="text-sm text-gray-500 mt-2">
              {{ smsCountdown }}秒后可重新发送
            </p>
            <p v-else class="text-sm text-gray-500 mt-2">
              <el-link type="primary" @click="sendSmsVerification">重新发送验证码</el-link>
            </p>
          </div>

          <p v-if="smsError" class="text-red-500 text-sm mb-4">{{ smsError }}</p>

          <el-button
            type="primary"
            @click="verifyCode"
            :loading="loading"
            class="w-full"
          >
            验证
          </el-button>
        </div>
      </div>
      <template #footer>
        <el-button @click="closeSmsModal">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { getAdminApiUrl } from "@/api/luckdraw.js";
import { getUserToken } from "@/utils/auth.js";
import {
  buildVerificationDocumentPreview,
  buildVerificationAuditRequest,
  getVerificationDescriptionColumn,
  normalizeVerificationApplication,
} from "./verificationPresenter.js";

export default {
  name: "VerificationDetail",
  data() {
    return {
      application: null,
      loading: false,
      showRejectModal: false,
      rejectReason: "",
      rejectError: "",
      selectedPresetReason: "",
      presetReasons: [
        "请提交运营人员真实姓名",
        "企业营业执照信息不准确"
      ],
      showSmsModal: false,
      verificationCode: "",
      smsError: "",
      isSmsSent: false,
      smsCountdown: 0,
      showDocumentModal: false,
      previewDocument: null,
      viewportWidth: window.innerWidth,
      verifiedApplications: new Set()
    };
  },
  computed: {
    descriptionColumn() {
      return getVerificationDescriptionColumn(this.viewportWidth);
    }
  },
  mounted() {
    this.loadApplication();
    window.addEventListener("resize", this.updateViewportWidth);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateViewportWidth);
  },
  methods: {
    updateViewportWidth() {
      this.viewportWidth = window.innerWidth;
    },

    async loadApplication() {
      const id = this.$route.params.id;
      if (!id) {
        this.$message.error('缺少申请ID');
        return;
      }

      this.loading = true;
      try {
        const token = getUserToken();
        const url = new URL(getAdminApiUrl('/auth/list'));
        url.searchParams.append('page', 1);
        url.searchParams.append('limit', 1);
        // 根据ID筛选
        url.searchParams.append('unionid', id);

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Token': `${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        const result = await response.json();

        if (result.sta === 0 && result.data.list && result.data.list.length > 0) {
          const app = result.data.list[0];
          this.application = normalizeVerificationApplication(app);
        } else {
          this.$message.error('未找到该认证申请');
        }
      } catch (error) {
        console.error('加载认证申请详情失败:', error);
        this.$message.error('加载失败');
      } finally {
        this.loading = false;
      }
    },

    getTypeLabel(type) {
      const typeMap = {
        1: '个人认证',
        2: '企业认证',
        3: '组织认证',
        4: '公众号认证'
      };
      return typeMap[type] || type;
    },

    getStatusLabel(status) {
      const statusMap = {
        1: '待审核',
        2: '已通过',
        3: '已拒绝'
      };
      return statusMap[status] || status;
    },

    getTypeTagType(type) {
      const tagTypes = {
        1: 'info',
        2: 'primary',
        3: 'primary',
        4: 'success'
      };
      return tagTypes[type] || 'info';
    },

    getStatusTagType(status) {
      const tagTypes = {
        0: 'warning',
        2: 'success',
        3: 'danger'
      };
      return tagTypes[status] || 'info';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString * 1000);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    openDocumentModal(document) {
      if (!document || !document.url) {
        this.$message.warning("暂无可查看的凭证");
        return;
      }

      const preview = buildVerificationDocumentPreview(document);
      this.previewDocument = preview.document;
      this.showDocumentModal = preview.visible;
    },

    closeDocumentModal() {
      this.showDocumentModal = false;
      this.previewDocument = null;
    },

    isImageDocument(document) {
      return /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(document.url || "");
    },

    async approveApplication() {
      if (!this.application) return;

      // 提交前确认
      if (!confirm(`确定要通过该认证申请吗？`)) {
        return;
      }

      this.loading = true;
      try {
        const token = getUserToken();
        const auditRequest = buildVerificationAuditRequest(
          this.application,
          'passed'
        );
        const response = await fetch(auditRequest.endpoint, {
          method: 'POST',
          headers: {
            'Token': `${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(auditRequest.body),
          credentials: 'include'
        });

        const result = await response.json();
        if (result.sta === 0) {
          this.application.status = 2;
          this.$message.success('申请已通过！');
        } else {
          throw new Error(result.msg || '操作失败');
        }
      } catch (error) {
        console.error('操作失败:', error);
        this.$message.error('操作失败，请重试');
      } finally {
        this.loading = false;
      }
    },

    openRejectModal() {
      this.rejectReason = "";
      this.rejectError = "";
      this.selectedPresetReason = "";
      this.showRejectModal = true;
    },

    closeRejectModal() {
      this.showRejectModal = false;
      this.rejectReason = "";
      this.rejectError = "";
      this.selectedPresetReason = "";
    },

    handlePresetReasonChange(value) {
      if (value) {
        this.rejectReason = value;
      }
    },

    async confirmReject() {
      if (!this.rejectReason.trim()) {
        this.rejectError = "请输入拒绝理由";
        return;
      }

      // 提交前确认
      if (!confirm(`确定要拒绝该认证申请吗？\n\n拒绝理由：${this.rejectReason}`)) {
        return;
      }

      this.loading = true;
      try {
        const token = getUserToken();
        const auditRequest = buildVerificationAuditRequest(
          this.application,
          'rejected',
          this.rejectReason
        );
        const response = await fetch(auditRequest.endpoint, {
          method: 'POST',
          headers: {
            'Token': `${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(auditRequest.body),
          credentials: 'include'
        });

        const result = await response.json();
        if (result.sta === 0) {
          this.application.status = 3;
          this.application.reason = this.rejectReason.trim();
          this.closeRejectModal();
          this.$message.success('申请已拒绝！');
        } else {
          throw new Error(result.msg || '操作失败');
        }
      } catch (error) {
        console.error('操作失败:', error);
        this.rejectError = '操作失败，请重试';
      } finally {
        this.loading = false;
      }
    },

    openSmsModal() {
      this.showSmsModal = true;
      this.isSmsSent = false;
      this.verificationCode = "";
      this.smsError = "";
    },

    closeSmsModal() {
      this.showSmsModal = false;
      this.isSmsSent = false;
      this.verificationCode = "";
      this.smsError = "";
      this.smsCountdown = 0;
    },

    async sendSmsVerification() {
      if (!this.application || !this.application.phone) {
        this.smsError = "该申请未提供有效的手机号码";
        return;
      }

      this.loading = true;
      this.smsError = "";

      try {
        const token = getUserToken();
        const response = await fetch(getAdminApiUrl(`/verification/applications/${this.application.id}/send-sms`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        const result = await response.json();
        if (result.sta === 0) {
          this.isSmsSent = true;
          this.smsCountdown = 60;
          const countdownInterval = setInterval(() => {
            this.smsCountdown--;
            if (this.smsCountdown <= 0) {
              clearInterval(countdownInterval);
            }
          }, 1000);
          this.$message.success("验证码已发送到手机");
        } else {
          throw new Error(result.msg || '发送失败');
        }
      } catch (error) {
        this.smsError = "发送验证码失败，请重试";
      } finally {
        this.loading = false;
      }
    },

    async verifyCode() {
      if (!this.verificationCode || this.verificationCode.length !== 6) {
        this.smsError = "请输入6位验证码";
        return;
      }

      this.loading = true;
      this.smsError = "";

      try {
        const token = getUserToken();
        const response = await fetch(getAdminApiUrl(`/verification/applications/${this.application.id}/verify-sms`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ code: this.verificationCode }),
          credentials: 'include'
        });

        const result = await response.json();
        if (result.sta === 0) {
          this.verifiedApplications.add(this.application.id);
          this.$message.success("验证成功！现在可以审核此申请");
          this.closeSmsModal();
        } else {
          this.smsError = result.msg || "验证码错误";
        }
      } catch (error) {
        this.smsError = "验证失败，请重试";
      } finally {
        this.loading = false;
      }
    },

    isApplicationVerified() {
      return this.verifiedApplications.has(this.application?.id);
    }
  }
};
</script>

<style scoped>
.verification-descriptions :deep(.el-descriptions__content) {
  word-break: break-word;
}

.verification-descriptions :deep(.el-descriptions__label) {
  white-space: normal;
}

@media (max-width: 767px) {
  .verification-detail-title {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
  }

  .verification-detail-meta {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.75rem;
  }

  .verification-descriptions :deep(.el-descriptions__label) {
    width: 88px;
    min-width: 88px;
  }

  .verification-descriptions :deep(.el-descriptions__content) {
    min-width: 0;
    overflow-wrap: anywhere;
  }
}
</style>
